import React, { Suspense, useCallback, useMemo } from 'react';
import lowerCase from 'lodash/lowerCase';

import { useTranslation } from '@lyearn/i18n';
import { Stack } from '@lyearn/ui';
import FormLayout from '@/components/Form/components/FormLayout';
import { AssignmentRestrictionTypeEnum } from '@/types/schema';

import SettingsTabs from '../VisibilitySettings/components/VisibilitySettingsTabs';
import SpecificRoles from './components/SpecificRoles';
import { DEFAULT_ASSIGNMENT_RESTRICTION } from './consts';
import { AssignmentRestrictionProps } from './types';
import { getSettingTabTypes } from './utils';

const AssignmentRestriction: React.FC<AssignmentRestrictionProps> = (props) => {
  const { t } = useTranslation();
  const tabs = useMemo(() => getSettingTabTypes(t), [t]);

  const {
    className,
    assignmentRestriction = DEFAULT_ASSIGNMENT_RESTRICTION,
    onChange,
    overrides,
    contentName,
  } = props;

  const updatedAssignmentRestriction = assignmentRestriction;
  const { type, roleIds } = updatedAssignmentRestriction;

  const onTypeChange = useCallback(
    (nextType: string) => {
      const updatedType = nextType as AssignmentRestrictionTypeEnum;
      onChange(
        updatedType === AssignmentRestrictionTypeEnum.All
          ? { type: updatedType }
          : {
              ...updatedAssignmentRestriction,
              type: updatedType,
            },
      );
    },
    [onChange, updatedAssignmentRestriction],
  );

  const onRoleIdChange = useCallback(
    (nextRoleIds: string[]) => {
      onChange({ ...updatedAssignmentRestriction, roleIds: nextRoleIds });
    },
    [onChange, updatedAssignmentRestriction],
  );

  return (
    <Stack className={className} direction="col">
      <SettingsTabs
        selectedTab={type}
        tabs={tabs}
        onTabChange={onTypeChange}
        {...overrides?.settingsTabs}
      />
      {type === AssignmentRestrictionTypeEnum.Restricted ? (
        <Suspense fallback={null}>
          <SpecificRoles
            className="mt-40"
            contentName={contentName}
            staffRoleIds={roleIds}
            onChange={onRoleIdChange}
          />
        </Suspense>
      ) : null}
    </Stack>
  );
};

export const FormAssignmentRestriction: React.FC<AssignmentRestrictionProps> = React.memo(
  (props) => {
    const { t } = useTranslation('AssignmentRestriction');
    const { className, ...rest } = props;

    return (
      <FormLayout className={className}>
        <FormLayout.Title>{t('Common:Form.AssignmentSettings')}</FormLayout.Title>
        <FormLayout.Description>
          {t('AssignmentRestriction:AssignmentSettingsDescription', {
            contentName: lowerCase(props?.contentName),
          })}
        </FormLayout.Description>
        <AssignmentRestriction {...rest} />
      </FormLayout>
    );
  },
);

export default React.memo(AssignmentRestriction);
